<template>
  <div class="content" :key="timer">
    <!-- 核心修改：补充鼠标事件监听 -->
    <div
      class="close-img"
      v-if="!showPopup && isGameLaunch"
      style="z-index: 9999999"
      :style="{ top: dragTop + 'px', left: dragLeft + 'px' }"
      @touchstart="handleDragStart"
      @touchmove="handleDragMove"
      @mousedown="handleDragStart"
      @mousemove="handleDragMove"
    >
      <img
        @click="goback"
        src="../../../assets/img/common/back.png"
        width="20"
      />
      <p>{{ $lang.common_txt62 }}</p>
    </div>

    <common-popup
      v-if="isGameLaunch"
      v-model="showPopup"
      :config="{
        isTitle: true,
        title: 'dica',
        content: textContent,
        bt1: btn1Text,
        bt2: btn2Text
      }"
      @close="close"
      @confirm="confirm"
    ></common-popup>

    <div v-if="isGameLaunch" class="iframe-wrapper">
      <iframe
        class="iframe-wrapper-s"
        v-if="isGameLaunch && forwardUrl"
        :src="forwardUrl"
        ref="myIframe"
        :style="iframeStyle"
      ></iframe>
    </div>

    <iframe
      v-if="safeRouteFrameUrl"
      :src="safeRouteFrameUrl"
      scrolling="auto"
      style="width: 100%; height: 100%"
      frameborder="0"
    ></iframe>
  </div>
</template>

<script>
import { GameBalanceList, ForwardGame } from '@/api/common'
import {
  HALL_LAUNCH_STORAGE,
  getLaunchUrl,
  hasBackendLaunchProof,
  hasLaunchUnavailableMarker,
  isGameLaunchReady,
  isLaunchUnavailableToken,
  launchUnavailableMessage,
  normalizeDemoValue
} from '@/components/home/gameLaunch'
import { contentOf, failMessage, handleNeedLogin, hasUnavailableMarker, isOk, safeParseJson, toNumber } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage, safeSessionStorage, safeSetStorageItem } from '@/utils/safeStorage'

function gameHallFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

function isSafeH5FrameUrl(url) {
  const value = String(url == null ? '' : url).trim()
  if (!value) return false
  const token = value.toLowerCase()
  if (
    token === '#' ||
    token === 'about:blank' ||
    token === 'javascript:void(0)' ||
    token.startsWith('javascript:') ||
    token.startsWith('vbscript:') ||
    token.startsWith('data:') ||
    token.includes('mock') ||
    token.includes('stub') ||
    token.includes('fallback') ||
    token.includes('read_only') ||
    token.includes('read-only') ||
    token.includes('read only')
  ) return false
  try {
    const parsed = new URL(value)
    return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname)
  } catch (e) {
    return false
  }
}

// #4 修:iframe URL 改从 sessionStorage 读(legit 跳转前按 from 分键写入),不再读
//        可被攻击者构造的 query.url/otherUrl → 切断"构造 gameHall?url=evil 反射钓鱼"向量
//        (sessionStorage 同源同标签页,外部链接无法预置受害者的 sessionStorage)。
//        合法 URL 含第三方支付网关/客服域,故不能用域白名单;defense-in-depth 仍过 isSafeH5FrameUrl。
function normalizeRouteFrameUrl(query = {}) {
  const from = String(query.from || '').trim()
  if (['recharge', 'home', 'what'].indexOf(from) === -1) return ''
  let rawUrl = ''
  try { rawUrl = window.sessionStorage.getItem('gameHallFrameUrl:' + from) || '' } catch (e) { rawUrl = '' }
  const value = String(rawUrl == null ? '' : rawUrl).trim()
  return isSafeH5FrameUrl(value) ? value : ''
}

function fallbackToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasReadFallbackMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasReadFallbackMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const keyToken = fallbackToken(key)
    const valueToken = fallbackToken(value)
    if (keyToken.includes('fallback') && (value === true || value === 1 || ['true', '1', 'yes', 'on'].includes(valueToken) || valueToken.includes('fallback'))) return true
    if (value && typeof value === 'object') return hasReadFallbackMarker(value, depth + 1, seen)
    return valueToken.includes('fallback')
  })
}

function isReadOk(res) {
  return isOk(res) && !hasUnavailableMarker(res) && !hasReadFallbackMarker(res)
}

const READ_ENVELOPE_KEYS = ['content', 'data', 'payload', 'page', 'body', 'records']
const BALANCE_KEYS = ['balance', 'totalBalance', '_balUsable', 'balUsable', 'availableBalance']

function normalizeReadEnvelope(payload, depth = 0, seen = []) {
  const content = contentOf(payload)
  const source = content && typeof content === 'object' ? content : {}
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return source
  seen.push(payload)
  return READ_ENVELOPE_KEYS.reduce((merged, key) => {
    const value = payload[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) return merged
    const nested = normalizeReadEnvelope(value, depth + 1, seen)
    return Object.keys(nested).length ? { ...merged, ...nested } : merged
  }, source)
}

function firstDefinedValue(source, keys) {
  const payload = source && typeof source === 'object' ? source : {}
  const key = keys.find(name => payload[name] !== undefined && payload[name] !== null && payload[name] !== '')
  return key ? payload[key] : 0
}

function readGameHallBalanceAmount(payload) {
  return toNumber(firstDefinedValue(normalizeReadEnvelope(payload), BALANCE_KEYS))
}

function hasLaunchProofGuard(payload) {
  return typeof hasBackendLaunchProof !== 'function' || hasBackendLaunchProof(payload)
}

function hasWindow() {
  try {
    return typeof window !== 'undefined'
  } catch (e) {
    return false
  }
}

function getBrowserWindow() {
  try {
    return hasWindow() ? window : null
  } catch (e) {
    return null
  }
}

function getBrowserDocument() {
  try {
    return typeof document !== 'undefined' ? document : null
  } catch (e) {
    return null
  }
}

function getVisualViewport() {
  try {
    const win = getBrowserWindow()
    return win && win.visualViewport ? win.visualViewport : null
  } catch (e) {
    return null
  }
}

function addEventTargetListener(target, type, handler) {
  try {
    if (target && typeof target.addEventListener === 'function') {
      target.addEventListener(type, handler)
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

function removeEventTargetListener(target, type, handler) {
  try {
    if (target && typeof target.removeEventListener === 'function') {
      target.removeEventListener(type, handler)
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

function getLocalStorage() {
  try {
    return typeof safeLocalStorage === 'function' ? safeLocalStorage() : null
  } catch (e) {
    return null
  }
}

function getSessionStorage() {
  try {
    return typeof safeSessionStorage === 'function' ? safeSessionStorage() : null
  } catch (e) {
    return null
  }
}

export default {
  name: 'GameHall',
  components: {},
  data() {
    return {
      iframeStyle: {
        height: '0px',
        marginTop: '0px'
      },
      showPopup: false,
      balance: 0,
      forwardUrl: '',
      textContent: '',
      btn1Text: '',
      btn2Text: '',
      timer: null,
      launchPayload: {},
      isRechargeGame: false,
      isWirthgeGame: false,
      // 拖动相关数据
      dragTop: 5,
      dragLeft: 5,
      startX: 0,
      startY: 0,
      startTop: 0,
      startLeft: 0,
      // 新增：标记是否正在拖动
      isDragging: false
    }
  },
  computed: {
    isGameLaunch() {
      return this.launchFrom === 'game'
    },
    launchFrom() {
      return this.firstValue(
        this.launchPayload.from,
        this.$route.params.from,
        this.$route.query.from,
        safeGetStorageItem(getLocalStorage(), 'hallFrome'),
        safeGetStorageItem(getLocalStorage(), 'hallFrom')
      )
    },
    launchGid() {
      return this.firstValue(
        this.launchPayload.gid,
        this.$route.params.gid,
        this.$route.query.gid,
        safeGetStorageItem(getLocalStorage(), 'hallGid')
      )
    },
    launchTypeCate() {
      const value = this.firstValue(
        this.launchPayload.typeCate,
        this.$route.params.typeCate,
        this.$route.query.typeCate,
        safeGetStorageItem(getLocalStorage(), 'hallTypeCate')
      )
      return Number(value) || 1
    },
    launchGameTab() {
      return this.firstValue(
        this.launchPayload.gameTab,
        this.$route.params.gameTab,
        this.$route.query.gameTab,
        safeGetStorageItem(getLocalStorage(), 'hallGameTab')
      )
    },
    safeRouteFrameUrl() {
      return normalizeRouteFrameUrl(this.$route.query)
    }
  },
  mounted() {
    this.restoreLaunchPayload()
    if (this.isGameLaunch && !this.launchGid) {
      this.showLaunchError(this.$lang.common_txt145 || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.')
    } else if (this.token && this.isGameLaunch) {
      this.adjustIframeHeight()
      this.addEventListeners()
      const win = getBrowserWindow()
      addEventTargetListener(win, 'resize', this.setIframeHeight)
      this.GetGameBalanceList()
    } else if (this.isGameLaunch) {
      this.showNeedLogin()
    }
    // 新增：监听鼠标松开事件（全局，防止鼠标移出元素后无法结束拖动）
    const win = getBrowserWindow()
    if (win) {
      addEventTargetListener(win, 'mouseup', this.handleDragEnd)
      addEventTargetListener(win, 'touchend', this.handleDragEnd)
    }
  },
  beforeDestroy() {
    this.removeEventListeners()
    // 新增：移除全局鼠标松开监听
    const win = getBrowserWindow()
    if (win) {
      removeEventTargetListener(win, 'resize', this.setIframeHeight)
      removeEventTargetListener(win, 'mouseup', this.handleDragEnd)
      removeEventTargetListener(win, 'touchend', this.handleDragEnd)
    }
  },

  methods: {
    firstValue(...values) {
      const hit = values.find((value) => value !== undefined && value !== null && value !== '')
      return hit === undefined ? '' : hit
    },
    readLaunchStorage(storage) {
      return safeParseJson(safeGetStorageItem(storage, HALL_LAUNCH_STORAGE), {})
    },
    restoreLaunchPayload() {
      const stored = {
        ...this.readLaunchStorage(getLocalStorage()),
        ...this.readLaunchStorage(getSessionStorage())
      }
      this.launchPayload = {
        ...stored,
        ...this.$route.query,
        ...this.$route.params,
        from: this.firstValue(
          this.$route.params.from,
          this.$route.query.from,
          stored.from,
          safeGetStorageItem(getLocalStorage(), 'hallFrome'),
          safeGetStorageItem(getLocalStorage(), 'hallFrom')
        ),
        gid: this.firstValue(
          this.$route.params.gid,
          this.$route.query.gid,
          stored.gid,
          safeGetStorageItem(getLocalStorage(), 'hallGid')
        ),
        isDemo: normalizeDemoValue(
          this.firstValue(
            this.$route.params.isDemo,
            this.$route.query.isDemo,
            this.$route.query.demo,
            stored.isDemo,
            safeGetStorageItem(getLocalStorage(), 'hallIsDemo')
          )
        )
      }
      if (this.launchPayload.gid) safeSetStorageItem(getLocalStorage(), 'hallGid', String(this.launchPayload.gid))
      if (this.launchPayload.from) {
        safeSetStorageItem(getLocalStorage(), 'hallFrome', this.launchPayload.from)
        safeSetStorageItem(getLocalStorage(), 'hallFrom', this.launchPayload.from)
      }
      if (this.launchPayload.typeCate != null) {
        safeSetStorageItem(getLocalStorage(), 'hallTypeCate', String(this.launchPayload.typeCate))
      }
      if (this.launchPayload.gameTab != null) {
        safeSetStorageItem(getLocalStorage(), 'hallGameTab', String(this.launchPayload.gameTab))
      }
      this.timer = Date.now()
    },
    showNeedLogin() {
      this.$toast({
        message: this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now',
        icon: 'fail'
      })
      this.$jumpTo('/home', { isValue: 'isLogin' }, { replace: true })
    },
    showLaunchError(message) {
      this.showPopup = true
      this.btn1Text = 'isClose'
      this.btn2Text = 'Confirm'
      this.textContent = message || this.$lang.common_txt160 || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.'
    },
    getForwardUrl(content = {}) {
      const url = this.firstValue(
        content.forwardUrl,
        content.url,
        content.gameUrl,
        content.launchUrl,
        content.playUrl
      )
      const token = String(url == null ? '' : url).trim().toLowerCase()
      if (
        !token ||
        isLaunchUnavailableToken(token) ||
        ['undefined', 'null', 'false', '{}', '[]', 'about:blank', '#'].includes(token)
      ) {
        return ''
      }
      return url
    },
    // 合并：处理拖动开始（兼容触摸和鼠标）
    handleDragStart(e) {
      // 区分触摸和鼠标事件
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY

      this.startX = clientX
      this.startY = clientY
      this.startTop = this.dragTop
      this.startLeft = this.dragLeft
      // 标记开始拖动
      this.isDragging = true
      e.stopPropagation()
    },
    // 合并：处理拖动移动（兼容触摸和鼠标）
    handleDragMove(e) {
      try {
        // 只有拖动中才执行逻辑
        if (!this.isDragging) return
        const win = getBrowserWindow()
        const doc = getBrowserDocument()
        if (!win || !doc) return

        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        const clientY = e.touches ? e.touches[0].clientY : e.clientY

        // 计算移动距离
        const moveX = clientX - this.startX
        const moveY = clientY - this.startY

        // 计算新位置
        let newLeft = this.startLeft + moveX
        let newTop = this.startTop + moveY

        // 获取元素实际尺寸（避免硬编码，更灵活）
        const el = doc.querySelector('.close-img')
        if (!el) return
        const elWidth = el.offsetWidth
        const elHeight = el.offsetHeight
        const screenWidth = win.innerWidth
        const screenHeight = win.innerHeight

        // 边界限制：不超出屏幕
        newLeft = Math.max(0, Math.min(newLeft, screenWidth - elWidth))
        newTop = Math.max(0, Math.min(newTop, screenHeight - elHeight))

        // 更新位置
        this.dragLeft = newLeft
        this.dragTop = newTop

        // 阻止默认行为（防止滚动）
        e.preventDefault()
        e.stopPropagation()
      } catch (error) {
        this.isDragging = false
      }
    },
    // 新增：处理拖动结束
    handleDragEnd() {
      this.isDragging = false
    },

    adjustIframeHeight() {
      try {
        const win = getBrowserWindow()
        if (!win) return
        const viewport = getVisualViewport()
        if (viewport) {
          this.iframeStyle = {
            height: viewport.height + 'px',
            marginTop: viewport.offsetTop + 'px'
          }
        } else {
          this.iframeStyle.height = win.innerHeight + 'px'
        }
      } catch (e) {
        return false
      }
    },

    addEventListeners() {
      const win = getBrowserWindow()
      if (!win) return
      const viewport = getVisualViewport()
      if (viewport) {
        addEventTargetListener(viewport, 'resize', this.adjustIframeHeight)
        addEventTargetListener(viewport, 'scroll', this.adjustIframeHeight)
      } else {
        addEventTargetListener(win, 'resize', this.adjustIframeHeight)
      }
    },

    removeEventListeners() {
      const win = getBrowserWindow()
      if (!win) return
      const viewport = getVisualViewport()
      if (viewport) {
        removeEventTargetListener(viewport, 'resize', this.adjustIframeHeight)
        removeEventTargetListener(viewport, 'scroll', this.adjustIframeHeight)
      } else {
        removeEventTargetListener(win, 'resize', this.adjustIframeHeight)
      }
    },
    setViewHeight() {
      try {
        const win = getBrowserWindow()
        const doc = getBrowserDocument()
        if (!win || !doc) return
        if (!doc.documentElement || !doc.body) return
        const viewHeight =
          win.innerHeight / doc.documentElement.offsetHeight
        if (viewHeight < 0.9) {
          doc.body.style.paddingBottom = '50px'
        } else {
          doc.body.style.paddingBottom = '0px'
        }
      } catch (e) {
        return false
      }
    },
    async ForwardGame() {
      try {
        const data = await ForwardGame({
          gid: this.launchGid,
          gameCode: this.launchPayload.gameCode,
          isDemo: normalizeDemoValue(this.launchPayload.isDemo)
        })
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          return
        } else if (hasLaunchUnavailableMarker(data) || hasReadFallbackMarker(data)) {
          this.forwardUrl = ''
          this.showLaunchError(
            launchUnavailableMessage(data, this.$lang.common_txt160 || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.')
          )
        } else if (isReadOk(data)) {
          const content = contentOf(data)
          let closeGame = content.closeGame
          let needRecharge = content.needRecharge
          let vipLevel = content.vipLevel
          let onlyPg = content.onlyPg
          let wdFirst = content.wdFirst
          if (onlyPg) {
            this.showPopup = true
            this.btn1Text = 'isClose'
            this.btn2Text = 'Confirm'
            this.textContent = this.$lang.common_txt155
          } else if (wdFirst) {
            this.showPopup = true
            this.btn1Text = 'isClose'
            this.btn2Text = 'Confirm'
            this.textContent = this.$lang.common_txt156
            this.isWirthgeGame = true
          } else if (needRecharge) {
            this.showPopup = true
            this.btn1Text = 'isClose'
            this.btn2Text = 'Confirm'
            this.textContent = this.$lang.common_txt158
            this.isRechargeGame = true
          } else if (vipLevel) {
            this.showPopup = true
            this.btn1Text = 'isClose'
            this.btn2Text = 'Confirm'
            this.textContent = this.$lang.common_txt159
          } else if (closeGame) {
            this.showPopup = true
            this.btn1Text = 'isClose'
            this.btn2Text = 'Confirm'
            this.textContent = this.$lang.common_txt160
          } else {
            const url = getLaunchUrl(data) || this.getForwardUrl(content)
            if (!url) {
              this.showLaunchError(this.$lang.common_txt160 || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.')
              return
            }
            if (!isGameLaunchReady(data) || !hasLaunchProofGuard(data)) {
              this.showLaunchError(
                launchUnavailableMessage(
                  { status: 'ok', content: { launchProof: false } },
                  this.$lang.common_txt160 || this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.'
                )
              )
              return
            }
            this.forwardUrl = url
          }
        } else {
          this.forwardUrl = ''
          this.$toast({
            message: failMessage(data, this.$lang.common_txt160),
            icon: 'cross'
          })
        }
      } catch (e) {
        this.$toast({
          message: gameHallFailureMessage(e, this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.'),
          icon: 'cross'
        })
      }
    },
    async GetGameBalanceList() {
      try {
        const data = await GameBalanceList()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          return
        } else if (isReadOk(data)) {
          this.balance = readGameHallBalanceAmount(data)
          // 进入游戏前不再因余额为 0/过低拦截；是否可投注由游戏内/服务端投注接口校验
          this.$nextTick(() => {
            this.ForwardGame()
          })
        } else {
          this.forwardUrl = ''
          this.$toast({
            message: failMessage(data, this.$lang.common_txt160),
            icon: 'cross'
          })
        }
      } catch (e) {
        this.$toast({
          message: gameHallFailureMessage(e, this.$lang.feature_unavailable || 'This feature is temporarily unavailable. Please try again later.'),
          icon: 'cross'
        })
      }
    },
    goback() {
      if (
        this.$route.query.from === 'recharge' ||
        this.$route.query.from === 'home' ||
        this.$route.query.from === 'what'
      ) {
        this.$router.go(-1)
      }
      this.showPopup = true
      this.btn1Text = 'Cancel'
      this.btn2Text = 'Confirm'
      this.textContent = this.$lang.gameHall_txt5
    },
    close() {
      if (this.btn2Text === 'Confirm') {
        this.showPopup = false
      } else {
        this.$router.go(-1)
      }
    },
    confirm() {
      // 余额不足 → Recharge 按钮优先跳到充值页
      if (this.btn2Text === 'Recharge' || this.isRechargeGame) {
        this.$jumpTo('/rechargeCont', {}, { replace: true })
        return
      }
      if (this.launchTypeCate === 1) {
        this.$jumpTo(
          '/category',
          { gameTab: this.launchGameTab },
          { replace: true }
        )
      } else if (this.btn2Text === 'Confirm') {
        this.$jumpTo('/home', {}, { replace: true })
      } else {
        this.$jumpTo('/rechargeCont', {}, { replace: true })
      }
    }
  }
}
</script>

<style lang="less" scoped>
.content {
  width: 100%;
  height: 100vh;
  background: #ffffff !important;
}
.close-img {
  position: fixed;
  background: #1d567d;
  border-radius: 50%;
  padding: 8px;
  border: 1px solid;
  text-align: center;
  color: #ffffff;
  /* 优化PC端光标 */
  cursor: move;
  /* 禁止文本选中 */
  user-select: none;
  /* 禁止触摸默认行为 */
  touch-action: none;
}
.iframe-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom);
  height: calc(100vh - env(safe-area-inset-bottom));
  .iframe-wrapper-s {
    width: 100%;
    border: 0;
    display: block;
    overflow-y: auto;
  }
}
</style>
